'use client';

import Image from 'next/image';
import Icon from '@/app/ui/icons/Icon';
import { useNavigation } from '@/app/contexts/Navigation';
import { useState } from 'react';
import clsx from 'clsx';
import { lilita } from '@/app/ui/font';
import { usePathname } from 'next/navigation';
import { useAccount } from 'wagmi';
import { useWeb3Modal } from '@web3modal/scaffold-react';
import { toast } from 'react-toastify';
import LinkList from '@/app/ui/nav/LinkList';
import dynamic from 'next/dynamic';
import logo from './images/logo.webp';

const Button = dynamic(() => import('@/app/ui/nav/Button'), { ssr: false });

export default function Navbar() {
  const { refs } = useNavigation();
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const { address, isConnected } = useAccount();
  const { open: openAuth } = useWeb3Modal();

  const logoButtonProps =
    pathname === '/'
      ? {
          onClick: () => {
            if (refs && refs.hero) {
              refs.hero.scrollIntoView({
                behavior: 'smooth',
              });
            }
          },
          type: 'button' as const,
        }
      : {
          href: '/',
          type: 'link' as const,
        };

  const tokenButtonProps = isConnected
    ? {
        type: 'link' as const,
        href: '/purchase',
      }
    : {
        type: 'button' as const,
        onClick: () => openAuth(),
      };

  return (
    <nav className="fixed left-0 right-0 top-0 z-50 mx-auto mt-[10px] w-[80%] rounded-[24px] bg-[rgba(9,10,30,0.86)] px-[10px] backdrop-blur-[2.9px] lg:mt-0 lg:w-full lg:rounded-none lg:bg-[rgba(17,7,35,0.47)] lg:px-11 lg:py-0">
      <div className="flex h-[65px] w-full items-center justify-between px-4 lg:container lg:h-[104px] lg:gap-[20px] lg:px-0 xl:gap-[35px]">
        <div className="flex items-center">
          <div className="flex items-center">
            <Button {...logoButtonProps}>
              <Image
                alt="logo"
                src={logo}
                width={162}
                height={36}
                className="hidden lg:block"
              />
              <div className="block lg:hidden">
                <Icon name="smallLogo" width={52} height={33} />
              </div>
            </Button>
          </div>
        </div>
        {pathname === '/' && (
          <div className="hidden text-lg lg:flex lg:items-center lg:gap-[20px] xl:gap-[35px]">
            <div className="text-[rgba(3,154,248,0.39)] xl:hidden">|</div>
            <LinkList />
            <Button
              className="ml-[27px] flex h-[44px] w-[202px] items-center justify-center rounded-[73px] border-2 border-baby-blue bg-midnight-blue uppercase text-baby-blue backdrop-blur-[2px]"
              onClick={() => {
                if (refs && refs.hero) {
                  refs.hero.scrollIntoView({
                    behavior: 'smooth',
                  });
                  if (!isConnected) {
                    openAuth();
                  }
                }
              }}
            >
              Buy tokens
            </Button>
          </div>
        )}
        <div className="block max-w-[70%] lg:hidden">
          {pathname === '/' && (
            <Button
              {...tokenButtonProps}
              className="flex h-[44px] flex-1 items-center rounded-[71px] bg-baby-blue px-[27px] uppercase text-[#060A28] lg:hidden"
            >
              Buy tokens
            </Button>
          )}
          {pathname !== '/' && pathname !== '/form' && address && (
            <div className="flex w-full">
              <p className="quiroh-font flex-1 truncate text-pale-mist">
                {address}
              </p>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(address).then(() => {
                    toast('Copied!', { type: 'success' });
                  });
                }}
              >
                <Icon name="copy" width={25} height={25} />
              </Button>
            </div>
          )}
        </div>
        {pathname === '/' && (
          <div className="flex lg:hidden">
            <Button
              onClick={() => setOpen((prev) => !prev)}
              aria-label="Menu"
              className="text-baby-blue"
            >
              <Icon name="burger" width={30} height={17} />
            </Button>
          </div>
        )}
      </div>
      <div
        className={clsx(
          'overflow-y-hidden px-[55px] text-center transition-all duration-500 lg:hidden',
          {
            'max-h-[450px]': open,
            'max-h-0': !open,
          },
        )}
      >
        <LinkList
          className={`${lilita.className} w-full border-t border-t-[rgba(106,138,170,0.50)] py-4 text-[18px] uppercase`}
          onMobileClick={() => setOpen(false)}
        />
      </div>
    </nav>
  );
}
